import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import Pagination, { usePagination } from "@/components/Pagination";
import SkillCard from "@/components/SkillCard";
import { SuggestionStatus } from "@/components/SuggestionStatus";
import SuggestionView from "@/components/SuggestionView";
import TeamManage from "@/components/TeamManage";
import { createTeam, getDashboard, getSkill, setDefaultHidden } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { NAV, type NavKey } from "@/lib/dashboardNav";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { useDelayedFlag } from "@/lib/useDelayedFlag";
import SkillPipelineProgress from "@/components/SkillPipelineProgress";
import { SkillRejectionReport } from "@/components/SkillRejectionReport";
import type { Dashboard, Skill, SkillDetail, Suggestion } from "@/lib/types";

export default function DashboardPage() {
  useDocumentTitle("SkillPatch");
  const { user, accessToken } = useAuth();
  const [data, setData] = useState<Dashboard | null>(null);
  // Defer the loading placeholder so a fast load goes straight to content without a flash.
  const showLoading = useDelayedFlag(!data);
  const [searchParams, setSearchParams] = useSearchParams();
  const active: NavKey = (searchParams.get("section") as NavKey) || "dashboard";
  // Detail panels (open team / open suggestion) live in the URL so they survive a refresh.
  const openTeam = active === "teams" ? searchParams.get("team") : null;
  const sugId = searchParams.get("sug");
  const sugSlug = searchParams.get("sugSlug");
  const openSuggestion =
    active === "suggestions" && sugId && sugSlug
      ? { slug: sugSlug, id: sugId }
      : null;
  // Archived (rejected) skill opened for its report — kept in the URL so it survives a refresh
  // and stays inside the dashboard (sidebar preserved) instead of navigating to the skill page.
  const openArch = active === "archives" ? searchParams.get("arch") : null;
  const openArchive = (slug: string) => setSearchParams({ section: "archives", arch: slug });
  const openTeamPanel = (slug: string) => setSearchParams({ section: "teams", team: slug });
  const closeTeamPanel = () => setSearchParams({ section: "teams" });
  const openSug = (slug: string, id: string) => setSearchParams({ section: active, sug: id, sugSlug: slug });
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [teamBusy, setTeamBusy] = useState(false);
  const [teamErr, setTeamErr] = useState<string | null>(null);
  const [hideBusy, setHideBusy] = useState<string | null>(null);
  const [hideErr, setHideErr] = useState<string | null>(null);
  // Skill-suggestions section sub-tab: "mine" = changes I proposed, "received" = suggestions on my skills.
  const [sugTab, setSugTab] = useState<"mine" | "received">("mine");

  async function onCreateTeam(e: React.FormEvent) {
    e.preventDefault();
    if (!newTeamName.trim() || !accessToken) return;
    setTeamBusy(true);
    setTeamErr(null);
    try {
      const t = await createTeam(newTeamName.trim(), accessToken);
      setNewTeamName("");
      setCreatingTeam(false);
      await load();
      openTeamPanel(t.slug); // open the new team inline (to add members)
    } catch (err) {
      setTeamErr(err instanceof Error ? err.message : "Failed to create team");
    } finally {
      setTeamBusy(false);
    }
  }

  const load = useCallback(async () => {
    if (!accessToken) return;
    setData(await getDashboard(accessToken).catch(() => null));
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the in-memory dashboard consistent when a bookmark is toggled from ANY tab.
  // The dashboard is fetched once (no refetch on tab switch), so without this a skill
  // bookmarked from "My Skills" (or Shared/Contributions) would never appear on the
  // Bookmarks tab until a full reload. Flip the `bookmarked` flag wherever the skill
  // shows up, and add/remove it from the bookmarks list (newest-first).
  const onBookmark = useCallback((skill: Skill, bookmarked: boolean) => {
    setData((d) => {
      if (!d) return d;
      const setFlag = (list: Skill[]) =>
        list.map((x) => (x.id === skill.id ? { ...x, bookmarked } : x));
      let bookmarks = d.bookmarks;
      if (bookmarked) {
        if (!bookmarks.some((x) => x.id === skill.id))
          bookmarks = [{ ...skill, bookmarked: true }, ...bookmarks];
      } else {
        bookmarks = bookmarks.filter((x) => x.id !== skill.id);
      }
      return {
        ...d,
        skills: setFlag(d.skills),
        shared: setFlag(d.shared),
        contributions: setFlag(d.contributions),
        bookmarks: setFlag(bookmarks),
        defaults: d.defaults ? setFlag(d.defaults) : d.defaults,
      };
    });
  }, []);

  // Hide / bring back a default skill in MY library only (the agent sync follows on its next run).
  const onToggleHidden = useCallback(async (skill: Skill, hidden: boolean) => {
    if (!accessToken) return;
    setHideBusy(skill.id);
    setHideErr(null);
    try {
      await setDefaultHidden(skill.slug, hidden, accessToken);
      setData((d) =>
        d && d.defaults
          ? { ...d, defaults: d.defaults.map((x) => (x.id === skill.id ? { ...x, default_hidden: hidden } : x)) }
          : d,
      );
    } catch (err) {
      setHideErr(err instanceof Error ? err.message : "Couldn't update this default skill");
    } finally {
      setHideBusy(null);
    }
  }, [accessToken]);

  // Auth is enforced by the <RequireAuth> route guard, which redirects logged-out
  // users to /login and never mounts this page until the session is restored. This
  // stays only as a defensive / type-narrowing check.
  if (!user || !accessToken) return null;

  const detailOpen = !!(openTeam || openSuggestion);
  // Incoming suggestions (others → my skills), split into open/closed for the "Received" sub-tab.
  const incoming = data?.incoming ?? [];
  const openIncoming = incoming.filter((s) => s.status !== "merged" && s.status !== "rejected");
  // Tab pills show the number of *open* (pending) suggestions in each tab, not the total.
  const openMine = (data?.suggestions ?? []).filter((s) => s.status !== "merged" && s.status !== "rejected");
  const closeSuggestion = () => {
    setSearchParams({ section: active }); // drop sug params, stay on the current section
    load(); // refresh counts/lists after viewing or approving
  };
  const counts: Record<NavKey, number> = {
    dashboard: data?.skills.length ?? 0,
    archives: data?.rejected.length ?? 0,
    shared: data?.shared.length ?? 0,
    defaults: (data?.defaults ?? []).filter((s) => !s.default_hidden).length,
    contributions: data?.contributions.length ?? 0,
    bookmarks: data?.bookmarks.length ?? 0,
    suggestions: (data?.suggestions.length ?? 0) + openIncoming.length,
    teams: data?.teams.length ?? 0,
  };
  const META: Record<NavKey, { desc: string; action?: { label: string; href: string } }> = {
    dashboard: { desc: "Your skills, contributions, and activity at a glance." }, // greeting moved to the Home feed; "Publish skill" lives in the header nav
    archives: { desc: "Uploads that didn't pass the Scan → Security review — kept for a week, then removed." },
    shared: { desc: "Skills shared with you through a team or a direct grant." },
    defaults: { desc: "Skills your SkillPatch admin gives every account — read-only, and installed on your machine by setup. Hide any you don't want." },
    contributions: { desc: "Skills you've improved through merged suggestions." },
    bookmarks: { desc: "Skills you've saved to revisit later." },
    suggestions: { desc: "Changes you've proposed, and suggestions people made to your skills." },
    teams: { desc: "Teams you own or belong to." },
  };
  const meta = META[active];

  return (
    <>
          {!detailOpen && (
            <>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h1 className="flex items-center gap-2.5 font-display text-[1.7rem] font-bold leading-tight tracking-tight">
                    {NAV.find((n) => n.key === active)?.label}
                    {active !== "dashboard" && active !== "suggestions" && (
                      <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-sm font-semibold text-text/50">
                        {counts[active]}
                      </span>
                    )}
                  </h1>
                  <p className="mt-1 text-sm text-text/50">{meta.desc}</p>
                </div>
                {meta.action && (
                  <Link
                    to={meta.action.href}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm shadow-primary/20 transition hover:opacity-90"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    {meta.action.label}
                  </Link>
                )}
              </div>
            </>
          )}

          {!data ? (
            showLoading ? <p className="mt-10 text-sm text-text/40">Loading…</p> : null
          ) : (
            <div className={detailOpen ? "" : "mt-7"}>
              {active === "archives" && (
                openArch ? (
                  <ArchiveReport slug={openArch} token={accessToken} />
                ) : data.rejected.length === 0 ? (
                  <Empty icon="archives">
                    Uploads that don&apos;t pass the Scan → Security review appear here for a week, so you can see why.
                  </Empty>
                ) : (
                  <div className="space-y-3">
                    {data.rejected.map((s) => (
                      <SkillCard key={s.id} skill={s} showStatus showAge minimal onOpen={openArchive} />
                    ))}
                  </div>
                )
              )}

              {active === "shared" && (
                data.shared.length === 0 ? (
                  <Empty icon="share">No skills have been shared with you yet.</Empty>
                ) : (
                  <Grid>{data.shared.map((s) => <SkillCard key={s.id} skill={s} onBookmarkChange={(b) => onBookmark(s, b)} />)}</Grid>
                )
              )}

              {active === "defaults" && (
                <DefaultSkills
                  skills={data.defaults ?? []}
                  busyId={hideBusy}
                  error={hideErr}
                  onToggleHidden={onToggleHidden}
                  onBookmark={onBookmark}
                />
              )}

              {active === "contributions" && (
                data.contributions.length === 0 ? (
                  <Empty icon="merge">Skills you improve through merged suggestions will appear here.</Empty>
                ) : (
                  <Grid>{data.contributions.map((s) => <SkillCard key={s.id} skill={s} onBookmarkChange={(b) => onBookmark(s, b)} />)}</Grid>
                )
              )}

              {active === "bookmarks" && (
                data.bookmarks.length === 0 ? (
                  <Empty icon="bookmark" cta={{ label: "Browse skills", href: "/skills" }}>
                    No bookmarks yet — save skills you want to revisit.
                  </Empty>
                ) : (
                  <Grid>
                    {data.bookmarks.map((s) => (
                      <SkillCard
                        key={s.id}
                        skill={s}
                        onBookmarkChange={(b) => onBookmark(s, b)}
                      />
                    ))}
                  </Grid>
                )
              )}

              {active === "suggestions" && (
                openSuggestion ? (
                  <SuggestionView slug={openSuggestion.slug} id={openSuggestion.id} onBack={closeSuggestion} />
                ) : (
                  <div>
                    {/* Sub-tabs: My suggestions (I authored) · Received (on my skills). */}
                    <div className="mb-5 flex gap-6 border-b border-[var(--border)] text-sm">
                      <SugTabBtn active={sugTab === "mine"} onClick={() => setSugTab("mine")} label="My suggestions" count={openMine.length} />
                      <SugTabBtn active={sugTab === "received"} onClick={() => setSugTab("received")} label="Received" count={openIncoming.length} />
                    </div>

                    {sugTab === "mine" ? (
                      data.suggestions.length === 0 ? (
                        <Empty icon="suggest">You haven&apos;t proposed any changes yet.</Empty>
                      ) : (
                        <SuggestionGroups
                          items={data.suggestions}
                          onOpen={(s) => s.skill_slug && openSug(s.skill_slug, s.id)}
                          emptyOpen="No open suggestions."
                          emptyClosed="No closed suggestions yet."
                        />
                      )
                    ) : incoming.length === 0 ? (
                      <Empty icon="suggest">No one has suggested changes to your skills yet.</Empty>
                    ) : (
                      <SuggestionGroups
                        items={incoming}
                        onOpen={(s) => s.skill_slug && openSug(s.skill_slug, s.id)}
                        emptyOpen="Nothing waiting on you."
                        emptyClosed="No closed suggestions yet."
                      />
                    )}
                  </div>
                )
              )}

              {active === "teams" && (
                openTeam ? (
                  <TeamManage
                    slug={openTeam}
                    onBack={closeTeamPanel}
                    onDeleted={async () => { await load(); closeTeamPanel(); }}
                    onRenamed={() => load()}
                  />
                ) : (
                  <Grid>
                    {data.teams.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => openTeamPanel(t.slug)}
                        className="group flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-left transition hover:-translate-y-0.5 hover:border-primary/50 hover:bg-white/[0.04]"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/20 text-base font-semibold text-accent">
                          {t.name.charAt(0).toUpperCase()}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate font-semibold">{t.name}</span>
                          <span className="block text-xs text-text/40">
                            {t.member_ids.length} member{t.member_ids.length === 1 ? "" : "s"}
                          </span>
                        </span>
                      </button>
                    ))}
                    <button
                      onClick={() => { setTeamErr(null); setCreatingTeam(true); }}
                      className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 p-4 text-sm text-text/50 transition hover:border-primary/50 hover:text-text"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                      New team
                    </button>
                  </Grid>
                )
              )}
            </div>
          )}

      {/* Create-team modal */}
      {creatingTeam && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={() => !teamBusy && setCreatingTeam(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-white/10 bg-neutral-900 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-display text-lg font-semibold text-text">Create a team</h2>
            <p className="mt-1 text-sm text-text/55">Name it, then invite members and share skills with it.</p>
            <form onSubmit={onCreateTeam} className="mt-4 space-y-3">
              <input
                autoFocus
                placeholder="Team name (e.g. Platform)"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                className="w-full rounded-xl border border-white/15 bg-white/[0.015] px-3.5 py-2.5 text-sm outline-none transition focus:border-primary"
              />
              {teamErr && <p className="text-sm text-red-400">{teamErr}</p>}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setCreatingTeam(false)}
                  disabled={teamBusy}
                  className="rounded-xl border border-white/15 px-4 py-2.5 text-sm text-text/70 transition hover:bg-white/5 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={teamBusy || !newTeamName.trim()}
                  className="rounded-xl bg-gradient-to-r from-primary to-accent px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-primary/20 transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
                >
                  {teamBusy ? "Creating…" : "Create team"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// Default skills: the visible ones as cards (each with a Hide action), and the hidden ones in a
// compact list with a way back.
function DefaultSkills({
  skills,
  busyId,
  error,
  onToggleHidden,
  onBookmark,
}: {
  skills: Skill[];
  busyId: string | null;
  error: string | null;
  onToggleHidden: (s: Skill, hidden: boolean) => void;
  onBookmark: (s: Skill, bookmarked: boolean) => void;
}) {
  const visible = skills.filter((s) => !s.default_hidden);
  const hidden = skills.filter((s) => s.default_hidden);
  const btn =
    "rounded-lg border border-[var(--border)] px-2.5 py-1 text-xs text-text/60 transition hover:text-text disabled:opacity-50";
  return (
    <div className="space-y-6">
      {error && <p className="text-sm text-red-500">{error}</p>}
      {visible.length === 0 ? (
        <Empty icon="skill">
          {hidden.length > 0 ? "You've hidden every default skill." : "Your admin hasn't set any default skills yet."}
        </Empty>
      ) : (
        <Grid>
          {visible.map((s) => (
            <div key={s.id} className="flex flex-col gap-2">
              <SkillCard skill={s} onBookmarkChange={(b) => onBookmark(s, b)} />
              <div className="flex justify-end">
                <button disabled={busyId === s.id} onClick={() => onToggleHidden(s, true)} className={btn}>
                  Hide from my library
                </button>
              </div>
            </div>
          ))}
        </Grid>
      )}
      {hidden.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-semibold text-text/60">Hidden ({hidden.length})</h2>
          <ul className="divide-y divide-[var(--border)] rounded-xl border border-[var(--border)]">
            {hidden.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <Link to={`/skill/${s.slug}`} className="min-w-0 truncate text-sm text-text/80 hover:text-primary">
                  {s.name || s.slug}
                </Link>
                <button disabled={busyId === s.id} onClick={() => onToggleHidden(s, false)} className={btn}>
                  Show again
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{children}</div>;
}

const AVATAR_PALETTES = [
  ["bg-violet-500/20", "text-violet-400"],
  ["bg-blue-500/20", "text-blue-400"],
  ["bg-emerald-500/20", "text-emerald-400"],
  ["bg-orange-500/20", "text-orange-400"],
  ["bg-pink-500/20", "text-pink-400"],
  ["bg-cyan-500/20", "text-cyan-400"],
];

function slugAvatar(slug: string) {
  const idx = slug.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_PALETTES.length;
  const [bg, text] = AVATAR_PALETTES[idx];
  const initials = slug.split("-").slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");
  return { bg, text, initials };
}

function toUtc(iso: string) {
  return new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z");
}

function relTime(iso: string) {
  const m = Math.floor((Date.now() - toUtc(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** The report for an archived (rejected) skill, shown inline in the Archives section so the
 *  dashboard sidebar stays. Lazily fetches the skill detail (which carries the rejection report). */
function ArchiveReport({ slug, token }: { slug: string; token: string }) {
  const [detail, setDetail] = useState<SkillDetail | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setErr(false);
    getSkill(slug, token)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [slug, token]);

  if (err) return <p className="text-sm text-text/45">Couldn&apos;t load this archived skill.</p>;
  if (!detail) return <p className="text-sm text-text/40">Loading report…</p>;
  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-lg font-semibold first-letter:uppercase">{detail.name}</h2>
        <p className="mt-1 text-xs text-text/40" title={new Date(detail.updated_at).toLocaleString()}>
          Archived {relTime(detail.updated_at)} · automatically removed after 7 days
        </p>
      </div>
      {/* Phase stepper, then the full report below it — full width, not centered. */}
      <SkillPipelineProgress skill={detail} />
      {detail.rejection && (
        <SkillRejectionReport stage={detail.rejection.stage} report={detail.rejection.report} className="mt-6" />
      )}
    </div>
  );
}

/** A pending team invite in the Notifications tab — accept to join, decline to dismiss. */
// Renders a suggestion list split into Open (pending) and Closed (merged/rejected) groups.
// Shared by the "My suggestions" and "Received" sub-tabs so both look identical.
const SUG_PAGE_SIZE = 5;

function SuggestionGroups({ items, onOpen, emptyOpen, emptyClosed }: {
  items: Suggestion[];
  onOpen: (s: Suggestion) => void;
  emptyOpen: string;
  emptyClosed: string;
}) {
  // Memoized so each group's reference is stable — otherwise pagination resets to page 1 every render.
  const open = useMemo(() => items.filter((s) => s.status !== "merged" && s.status !== "rejected"), [items]);
  const closed = useMemo(() => items.filter((s) => s.status === "merged" || s.status === "rejected"), [items]);
  return (
    <div className="space-y-6">
      <SuggestionList title="Open" note="pending" items={open} onOpen={onOpen} emptyText={emptyOpen} />
      <SuggestionList title="Closed" note="approved or declined" items={closed} onOpen={onOpen} emptyText={emptyClosed} />
    </div>
  );
}

// A paginated suggestion list (5/page). Heading + prev/next control share one row; the control
// only appears once the list overflows a page.
function SuggestionList({ title, note, items, onOpen, emptyText }: {
  title: string;
  note: string;
  items: Suggestion[];
  onOpen: (s: Suggestion) => void;
  emptyText: string;
}) {
  const pager = usePagination(items, SUG_PAGE_SIZE);
  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="font-display text-sm font-semibold text-text/70">
          {title} <span className="ml-2 text-xs font-normal text-text/40">{note}</span>
        </h3>
        {pager.total > pager.pageSize && (
          <Pagination page={pager.page} pageSize={pager.pageSize} total={pager.total} onPage={pager.setPage} showSummary showNav={false} showSteps stepLabels />
        )}
      </div>
      {items.length === 0 ? (
        <p className="rounded-xl border border-white/10 bg-white/[0.015] p-5 text-sm text-text/45">{emptyText}</p>
      ) : (
        <ul className="space-y-2.5">
          {pager.paged.map((s) => <SugRow key={s.id} s={s} onOpen={() => onOpen(s)} />)}
        </ul>
      )}
    </div>
  );
}

function SugTabBtn({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button
      onClick={onClick}
      className={`-mb-px flex items-center gap-2 border-b-2 pb-2.5 font-medium transition ${
        active ? "border-primary text-text" : "border-transparent text-text/50 hover:text-text/80"
      }`}
    >
      {label}
      <span className={`rounded-full px-1.5 py-0.5 text-[11px] ${active ? "bg-primary/15 text-primary" : "bg-white/[0.06] text-text/50"}`}>{count}</span>
    </button>
  );
}

function SugRow({ s, onOpen }: { s: Suggestion; onOpen: () => void }) {
  const slug = s.skill_slug ?? "";
  const { bg, text, initials } = slugAvatar(slug);
  const isAgent = s.source === "agent";

  return (
    <li className="group relative rounded-xl border border-white/10 bg-white/[0.02] transition hover:border-primary/40 hover:bg-white/[0.04]">
      {/* Whole-card click opens the suggestion; interactive children opt back in with pointer-events-auto. */}
      <button
        type="button"
        onClick={onOpen}
        disabled={!s.skill_slug}
        aria-label={`Open suggestion${s.title ? `: ${s.title}` : ""}`}
        className="absolute inset-0 rounded-xl disabled:cursor-default"
      />
      <div className="pointer-events-none relative flex items-start gap-3.5 p-4">
        {/* Skill avatar */}
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-bold ${bg} ${text}`}>
          {initials || "?"}
        </span>

        <div className="min-w-0 flex-1">
          {/* Skill name + time — skill name matches the title's size & color for consistency. */}
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-semibold text-text">{slug || "—"}</span>
            <span className="shrink-0 text-xs text-text/35">{relTime(s.created_at)}</span>
          </div>

          {/* Title */}
          <p className="mt-1 truncate text-sm font-semibold text-text">
            {s.title || s.message}
          </p>

          {/* Meta row: source + inline status */}
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {isAgent ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-cyan-500/10 px-1.5 py-0.5 text-[11px] font-medium text-cyan-600 dark:text-cyan-400">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4M8 15h.01M16 15h.01" />
                </svg>
                {s.agent_name ?? "agent"}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                </svg>
                You
              </span>
            )}
            <SuggestionStatus s={s} expandable={false} />
          </div>
        </div>

        {/* Hover affordance — signals the whole card is clickable */}
        <svg
          className="mt-0.5 shrink-0 self-center text-text/30 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100"
          width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
      </div>
    </li>
  );
}

const EMPTY_ICON: Record<string, React.ReactNode> = {
  skill: <path d="M12 2l3 7h7l-5.5 4.5L18.5 21 12 16.5 5.5 21l2-7.5L2 9h7z" />,
  share: (
    <>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </>
  ),
  merge: (
    <>
      <circle cx="18" cy="18" r="3" />
      <circle cx="6" cy="6" r="3" />
      <path d="M6 21V9a9 9 0 0 0 9 9" />
    </>
  ),
  suggest: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  bookmark: <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />,
  archives: (
    <>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
};

function Empty({ icon, cta, children }: { icon?: string; cta?: { label: string; href: string }; children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 bg-white/[0.02] px-6 py-14 text-center">
      {icon && (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-3 text-text/30">
          {EMPTY_ICON[icon]}
        </svg>
      )}
      <p className="max-w-xs text-sm text-text/50">{children}</p>
      {cta && (
        <Link to={cta.href} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90">
          {cta.label}
        </Link>
      )}
    </div>
  );
}
